import {EquipmentData, EquipmentIdType} from '@domain/shifthandover'
import {useMainEquipmentFilterOptionsSearch} from '@hconnect/common/hooks'
import {filterSxStyles} from '@hconnect/common/styles'
import {getEquipmentLabel, areMainEquipmentsSame} from '@hconnect/common/utils'
import {Autocomplete, TextField, Paper, MenuItem} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_FILTERS_WIDGET_NAMESPACE} from '../consts'

export type Props = {
  'data-test-id'?: string
  label?: React.ReactNode
  onChange?: (term: EquipmentData | undefined) => void
  placeholder?: string
  value?: EquipmentData
  removeFilter: (keyName: string) => void
  idType?: EquipmentIdType
  plantId: string
}

const paper = (props) => <Paper elevation={8} style={{minWidth: '350px'}} {...props} />

export const MainEquipmentSearchField: React.FC<Props> = ({
  onChange = () => undefined,
  value: selectedEquipment,
  removeFilter,
  idType,
  plantId,
  ...props
}: Props) => {
  const {t} = useTranslation(SHIFT_EVENT_FILTERS_WIDGET_NAMESPACE)
  const [open, setOpen] = React.useState(false)

  const [searchTerm, setSelectedSearchTerm] = React.useState<string | undefined>(undefined)
  const {data: options} = useMainEquipmentFilterOptionsSearch(plantId, searchTerm, idType)

  const suggestions = useMemo(() => {
    if (!options) {
      return []
    }

    // filter out the selected item
    return options
      .map((plain) => plain)
      .filter((equipment) => equipment.id !== selectedEquipment?.id)
  }, [options, selectedEquipment])

  return (
    <Autocomplete
      data-test-id={props['data-test-id']}
      // it is a bit hacky, but the emptyItem will cause the clear button to be visible on hover even if nothing is selected
      // so the user can first clear the text or equipment and with a second click on the clear button remove the filter option
      value={selectedEquipment ?? null}
      onChange={(event, next, reason) => {
        if (reason === 'clear') {
          if (selectedEquipment || searchTerm) {
            // first rest the filter
            onChange(undefined)
            setSelectedSearchTerm(undefined)
          } else {
            // then remove it
            removeFilter('equipment')
          }
        } else if (next && typeof next !== 'string' && next.id !== selectedEquipment?.id) {
          // new selected equipment
          onChange(next)
        }
      }}
      onInputChange={(event, newValue, reason) => {
        if (reason === 'input' && newValue !== searchTerm) {
          setSelectedSearchTerm(newValue)
        }
      }}
      open={open && suggestions.length > 0}
      onOpen={() => {
        setOpen(true)
      }}
      onClose={() => {
        setOpen(false)
      }}
      options={suggestions}
      sx={{
        marginRight: {xs: 2, md: 0}
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label || t('shiftEvent.label.mainEquipment')}
          placeholder={t('pleaseStartTyping')}
          fullWidth={false}
          sx={filterSxStyles.textInputField}
          variant="filled"
        />
      )}
      PaperComponent={paper}
      forcePopupIcon={false}
      renderOption={(renderProps, option) => (
        <MenuItem {...renderProps} key={`${option.id}-${option.idType}-${option.text}`}>
          {option ? getEquipmentLabel(option) : '-'}
        </MenuItem>
      )}
      getOptionLabel={(equipment) => (equipment ? getEquipmentLabel(equipment, true) : '-')}
      // per default the auto-compleat component will use the getOptionLabel,
      // compare it with the given input text and will filter out all that do not match.
      // that is a problem when having sapNumbers were we remove the leading zeros
      // 00000008321003 will be rendered as 8321003 and therefor not match if the input is 0083
      // and will not be presented as an option
      // to avoid this behavior, we override the filterOptions function to force it to compare on object level
      filterOptions={(options) => options}
      isOptionEqualToValue={areMainEquipmentsSame}
    />
  )
}
